import moment from 'moment';
import { createSelector } from '@ngrx/store';
import { getOverviewState, OverviewState } from '../registration';
import { ReportShiftState } from '../reducers';
import { processReportData } from '@shared/*';
import { StatusType, TourStopAction, TrackerPhase } from '@models/*';
import isEqual from 'lodash/isEqual';
import { getCurrentStop } from './tour.selectors';

export const getReportShiftsState = createSelector(getOverviewState, (state: OverviewState) => state.reportShifts);
export const getReportShiftsEntities = createSelector(getReportShiftsState, (state: ReportShiftState) => state.entities);
export const getSelectedReportDay = createSelector(getReportShiftsState, (state: ReportShiftState) => state.selectedDay);
export const getReportShiftsLoading = createSelector(getReportShiftsState, (state: ReportShiftState) => state.loading);
export const getReportShiftUpdatesLoading = createSelector(getReportShiftsState, (state: ReportShiftState) => state.updatesLoading);

export const getSelectedReportShift = createSelector(
    getReportShiftsEntities,
    getSelectedReportDay,
    (entities, reportDay: string) => entities[reportDay]
);

export const getTodayShift = createSelector(
    getReportShiftsEntities,
    (entities) => {
        const shifts = entities[moment().startOf('day').toISOString(true)];

        if (!shifts || !shifts.length) {
            return [];
        }
        return processReportData(shifts);
    }
);

export const getSelectedReportShiftFormatted = createSelector(getSelectedReportShift, reportShift => {
    if (!reportShift || !reportShift.length) {
        return [];
    }
    return processReportData(reportShift);
});

export const getRunningShift = createSelector(getSelectedReportShiftFormatted, reportShift => {
    if (!reportShift || !reportShift.length) {
        return null;
    }
    const lastShift = reportShift.reduce((acc, elem) => {
        if (!acc) {
            return elem;
        }
        return (moment(elem.startedAt).diff(moment(acc.startedAt)) > 0) ? elem : acc;
    }, null);
    return (lastShift.status !== 'end') ? lastShift : null;
});

export const getTodayRunningShift = createSelector(getTodayShift, reportShift => {
    if (!reportShift || !reportShift.length) {
        return null;
    }
    return reportShift.find(item => item.status === 'start');
});

export const getCurrentTourStopTracking = createSelector(getTodayRunningShift, shift => {
    if (!shift || !shift.tourStopTrackings || !shift.tourStopTrackings.length) {
        return null;
    }
    const lastTourStopTracking = shift.tourStopTrackings.filter(item => item.status === 'start');
    return lastTourStopTracking.length > 0 ? lastTourStopTracking[0] : null;
});

export const getCurrentShiftBreak = createSelector(getRunningShift, shift => {
    if (!shift || !shift.shiftBreaks || !shift.shiftBreaks.length) {
        return null;
    }
    const lastShiftBreak = shift.shiftBreaks[shift.shiftBreaks.length - 1];
    return (lastShiftBreak.status === 'start') ? lastShiftBreak : null;
});

export const getCurrentReportShiftLoading = createSelector(
    getReportShiftsLoading,
    getSelectedReportDay,
    (loadingDays: string[], selectedDay: string) => loadingDays.includes(selectedDay)
);

export const getCurrentReportShiftUpdatesLoading = createSelector(
    getReportShiftUpdatesLoading,
    getSelectedReportDay,
    (updatingDays: string[], selectedDay: string) => updatingDays.includes(selectedDay)
);

/**
 * Derive the current stop's tracking phase by looking at the already finished trackings, verifying started trackings with respect to their
 * time ordering, and by checking if the current stop has been tracked
 */
export const getCurrentShiftPhase = createSelector(
    getCurrentStop,
    getRunningShift,
    (currentStop, runningShift) => {
        if (!currentStop) {
            return TrackerPhase.NA;
        }
        const hasUnfinishedActions = currentStop.actions.some((action: TourStopAction) => (
            action.status === StatusType.INPROGRESS ||
            action.status === StatusType.NEW
        ));

        if ((!runningShift || !runningShift.tourStopTrackings.length) && hasUnfinishedActions) {
            return TrackerPhase.START;
        }

        if (runningShift && runningShift.tourStopTrackings.length) {
            // currentTourStopTracking: the current shift has an active tour stop tracking on the current stop's location
            const currentTourStopTracking = runningShift.tourStopTrackings.find(tRegister =>
                isEqual(tRegister.locationGeo, currentStop.locationGeo) &&
                tRegister.status === 'start' &&
                tRegister.tourId === currentStop.tourId
            );
            const mostRecentTourStopTrackingStatus = runningShift.tourStopTrackings[runningShift.tourStopTrackings.length - 1].status;

            if (!hasUnfinishedActions && !currentTourStopTracking) {
                return TrackerPhase.COMPLETED;
            }
            if (currentTourStopTracking) {
                return TrackerPhase.END;
            }
            if (!currentTourStopTracking && mostRecentTourStopTrackingStatus === 'start') {
                return TrackerPhase.ANOTHER;
            }
            if (
                (!currentTourStopTracking && mostRecentTourStopTrackingStatus === 'end') &&
                hasUnfinishedActions
            ) {
                return TrackerPhase.START;
            }
            if (
                (!currentTourStopTracking && mostRecentTourStopTrackingStatus === 'end') &&
                !hasUnfinishedActions
            ) {
                return TrackerPhase.NA;
            }

            if (hasUnfinishedActions) {
                return TrackerPhase.START;
            }
        }

        return TrackerPhase.NA;
    }
);
